import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Megaphone, Copy, Mail, Loader2 } from "lucide-react";

const SITE_PITCH = "GPA+ is Lone Star College's Transferable Skills Benchmark — it measures and verifies the durable, real-world skills a GPA can't show, and connects that talent to employers.";

function defaultBody(link) {
  return `Hi,

I wanted to share something I think you'll find valuable. ${SITE_PITCH}

If you hire, partner with, or support local talent, take a look — and if you sign up or get in touch through my link below, they'll know I sent you:

${link}

Happy to tell you more about it.

Best,`;
}

export function ReferButton() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [subject, setSubject] = useState("An invitation to see GPA+ — the skills a GPA can't show");
  const [body, setBody] = useState("");

  const link = data ? `${window.location.origin}/?ref=${data.code}` : "";

  const load = async () => {
    try {
      const { data } = await api.get("/referrals/me");
      setData(data);
      setBody(defaultBody(`${window.location.origin}/?ref=${data.code}`));
    } catch {
      toast.error("Couldn't load your referral link. Please try again.");
    }
  };

  const onOpenChange = (v) => { setOpen(v); if (v && !data) load(); };

  const mailtoHref = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied.`); }
    catch { toast.error("Couldn't copy — please select and copy manually."); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm"
          className="rounded-full border-[#78be21]/50 font-semibold text-[#3d6b0e] hover:bg-[#78be21]/10"
          data-testid="refer-button" data-pdf-hide>
          <Megaphone className="mr-1.5 h-4 w-4" /> Spread the Word
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="refer-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#003057]">Tell a business owner about GPA+</DialogTitle>
          <DialogDescription>
            Invite employers, partners, or supporters. When they sign up or reach out through your link, we'll credit you.
          </DialogDescription>
        </DialogHeader>

        {!data ? (
          <div className="flex h-32 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#003057]" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-600" data-testid="refer-stat-signups">{data.signups} sign-ups</span>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-600" data-testid="refer-stat-supporters">{data.supporters} supporters</span>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Your referral link</label>
              <div className="mt-1 flex gap-2">
                <Input readOnly value={link} className="font-mono text-xs" data-testid="refer-link" />
                <Button variant="outline" size="sm" onClick={() => copy(link, "Link")} className="shrink-0 rounded-lg" data-testid="refer-copy-link">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Subject</label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" data-testid="refer-subject" />
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Message</label>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9}
                className="mt-1 w-full rounded-lg border border-slate-200 p-3 text-sm text-slate-700 focus:border-[#005291] focus:outline-none focus:ring-1 focus:ring-[#005291]"
                data-testid="refer-message" />
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" onClick={() => copy(body, "Message")} className="rounded-full font-semibold" data-testid="refer-copy-message">
                <Copy className="mr-1.5 h-4 w-4" /> Copy message
              </Button>
              <a href={mailtoHref} data-testid="refer-mailto">
                <Button className="rounded-full bg-[#003057] font-semibold text-white hover:bg-[#00243f]">
                  <Mail className="mr-1.5 h-4 w-4" /> Open in email app
                </Button>
              </a>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
